import { useState, useEffect, useCallback } from 'react';
import { supabase, CmsNews } from '@/lib/supabase';
import RichEditor from '@/components/feature/RichEditor';
import ImageUploader from '@/components/base/ImageUploader';

interface EditForm {
  title: string;
  category: string;
  summary: string;
  content: string;
  cover_image: string;
  author: string;
  pinned: boolean;
  read_time: string;
}

const CATEGORIES = ['公司新闻', '行业动态', '技术分享'];

export default function NewsCMS() {
  const [list, setList] = useState<CmsNews[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editData, setEditData] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast(msg);
    setToastType(type);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cms_news')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      setList(data || []);
    } catch {
      showToast('加载失败，请刷新重试', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleEdit = (item: CmsNews) => {
    setEditId(item.id);
    setEditData({
      title: item.title,
      category: item.category,
      summary: item.summary || '',
      content: item.content || '',
      cover_image: item.cover_image || '',
      author: item.author || '',
      pinned: item.pinned || false,
      read_time: item.read_time || '',
    });
  };

  const handleSave = async () => {
    if (!editId || !editData) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('cms_news')
        .update({
          title: editData.title,
          category: editData.category,
          summary: editData.summary,
          content: editData.content,
          cover_image: editData.cover_image,
          author: editData.author,
          pinned: editData.pinned,
          read_time: editData.read_time,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editId);
      if (error) throw error;
      showToast('保存成功');
      setEditId(null);
      setEditData(null);
      await fetchList();
    } catch {
      showToast('保存失败，请重试', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (item: CmsNews) => {
    try {
      const { error } = await supabase
        .from('cms_news')
        .update({
          is_published: !item.is_published,
          published_at: !item.is_published ? new Date().toISOString() : item.published_at,
          updated_at: new Date().toISOString(),
        })
        .eq('id', item.id);
      if (error) throw error;
      showToast(item.is_published ? '已取消发布' : '已发布');
      await fetchList();
    } catch {
      showToast('操作失败', 'error');
    }
  };

  const handleCreate = async () => {
    try {
      const { error } = await supabase.from('cms_news').insert({
        title: '新文章标题',
        category: '公司新闻',
        summary: '',
        content: '',
        author: '编辑',
        is_published: false,
        view_count: 0,
        pinned: false,
        slug: `news-${Date.now()}`,
        tags: [],
      });
      if (error) throw error;
      showToast('已创建新文章，请编辑内容');
      await fetchList();
    } catch {
      showToast('创建失败', 'error');
    }
  };

  const handleMoveOrder = async (item: CmsNews, direction: 'up' | 'down') => {
    const idx = list.findIndex((n) => n.id === item.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    const swapItem = list[swapIdx];
    try {
      await Promise.all([
        supabase.from('cms_news').update({ sort_order: swapItem.sort_order }).eq('id', item.id),
        supabase.from('cms_news').update({ sort_order: item.sort_order }).eq('id', swapItem.id),
      ]);
      await fetchList();
    } catch {
      showToast('排序失败', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除这篇文章吗？')) return;
    try {
      const { error } = await supabase.from('cms_news').delete().eq('id', id);
      if (error) throw error;
      showToast('已删除');
      await fetchList();
    } catch {
      showToast('删除失败', 'error');
    }
  };

  const publishedCount = list.filter((n) => n.is_published).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">新闻动态管理</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            共 {list.length} 篇文章 · {publishedCount} 篇已发布
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>新建文章
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
              <div className="flex gap-4">
                <div className="w-20 h-14 bg-gray-100 rounded-lg flex-shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-1/3"></div>
                  <div className="h-4 bg-gray-100 rounded w-2/3"></div>
                  <div className="h-3 bg-gray-100 rounded w-full"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {list.map((item, idx) => (
            <div
              key={item.id}
              className={`bg-white rounded-xl border p-5 transition-all ${
                item.is_published ? 'border-gray-100' : 'border-gray-200 opacity-70'
              }`}
            >
              {editId === item.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">分类</label>
                      <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                        value={editData?.category || ''}
                        onChange={(e) => setEditData((d) => d ? { ...d, category: e.target.value } : d)}
                      >
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">作者</label>
                      <input
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                        value={editData?.author || ''}
                        onChange={(e) => setEditData((d) => d ? { ...d, author: e.target.value } : d)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">标题</label>
                    <input
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                      value={editData?.title || ''}
                      onChange={(e) => setEditData((d) => d ? { ...d, title: e.target.value } : d)}
                    />
                  </div>
                  <ImageUploader
                    label="封面图片"
                    value={editData?.cover_image || ''}
                    onChange={(url) => setEditData((d) => d ? { ...d, cover_image: url } : d)}
                    placeholder="粘贴图片 URL，如 https://..."
                  />
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">摘要</label>
                    <textarea
                      rows={2}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 resize-none"
                      value={editData?.summary || ''}
                      onChange={(e) => setEditData((d) => d ? { ...d, summary: e.target.value } : d)}
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-400 mt-0.5">{(editData?.summary || '').length}/500</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">正文内容</label>
                    <RichEditor
                      value={editData?.content || ''}
                      onChange={(val) => setEditData((d) => d ? { ...d, content: val } : d)}
                      placeholder="请输入文章正文，支持排版、图片插入、字体设置等..."
                      minHeight={400}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editData?.pinned || false}
                        onChange={(e) => setEditData((d) => d ? { ...d, pinned: e.target.checked } : d)}
                        className="w-4 h-4 accent-teal-600"
                      />
                      <span className="text-sm text-gray-600">置顶显示</span>
                    </label>
                    <div className="flex-1">
                      <input
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                        value={editData?.read_time || ''}
                        onChange={(e) => setEditData((d) => d ? { ...d, read_time: e.target.value } : d)}
                        placeholder="阅读时长，如：5分钟"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={() => { setEditId(null); setEditData(null); }}
                      className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-4">
                  {item.cover_image && (
                    <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      <img src={item.cover_image} alt={item.title} className="w-full h-full object-cover object-top" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded">{item.category}</span>
                      {item.pinned && (
                        <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded">置顶</span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_published ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {item.is_published ? '已发布' : '草稿'}
                      </span>
                      <span className="text-xs text-gray-400">
                        {item.published_at ? new Date(item.published_at).toLocaleDateString('zh-CN') : '未发布'}
                      </span>
                      <span className="text-xs text-gray-400">阅读 {item.view_count || 0}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-1 line-clamp-1">{item.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">{item.summary}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleMoveOrder(item, 'up')}
                      disabled={idx === 0}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="上移"
                    >
                      <i className="ri-arrow-up-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleMoveOrder(item, 'down')}
                      disabled={idx === list.length - 1}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="下移"
                    >
                      <i className="ri-arrow-down-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                      title="编辑"
                    >
                      <i className="ri-edit-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleTogglePublish(item)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                      title={item.is_published ? '取消发布' : '发布'}
                    >
                      <i className={`${item.is_published ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                      title="删除"
                    >
                      <i className="ri-delete-bin-line text-base"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg z-50 ${toastType === 'error' ? 'bg-rose-600' : 'bg-gray-800'}`}>
          <div className="flex items-center gap-2">
            <i className={`${toastType === 'error' ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'} ${toastType === 'error' ? 'text-rose-200' : 'text-emerald-400'}`}></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
